import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, map } from 'rxjs';
import { IFile } from '../../../services/bucket.service';
import { EmbeddingStatus, EmbeddingStatusService } from '../../../services/embedding-status.service';

interface ChipView {
  kind: 'ready' | 'partial' | 'queued' | 'processing' | 'failed' | 'pending';
  label: string;
  hint: string;
}

/**
 * Status chip of one knowledge-base file: is it searchable for the agents
 * yet? Ready / partial / waiting (with queue position) / reading (page x of
 * y for split PDFs) / failed (reason in the tooltip). Lives in a table cell
 * of `app-files-table`; the state comes from EmbeddingStatusService, which
 * polls while anything is in progress.
 */
@Component({
  selector: 'app-file-indexing-status',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      *ngIf="view$ | async as view"
      class="index-chip"
      [ngClass]="'is-' + view.kind"
      [nbTooltip]="view.hint"
      nbTooltipPlacement="top"
    >
      <nb-icon *ngIf="view.kind === 'processing'" icon="sync-outline" class="al-spin"></nb-icon>
      <span *ngIf="view.kind !== 'processing'" class="index-chip__dot"></span>
      <span class="index-chip__label">{{ view.label }}</span>
    </span>
  `,
  styles: [
    `
      // Same compact chip as the placement tables' sync status: 22px, dot +
      // word, dark text variant on a tinted surface.
      .index-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        max-width: 100%;
        height: 22px;
        padding: 0 7px;
        border-radius: 999px;
        background-color: rgb(143 155 179 / 0.14);
        color: var(--al-text-muted);
        font-size: 0.71875rem;
        font-weight: 500;
        line-height: 1;
        white-space: nowrap;
        cursor: help;
      }
      .index-chip__label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .index-chip__dot {
        width: 6px;
        min-width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: rgb(143 155 179);
      }
      nb-icon {
        width: 12px;
        height: 12px;
        font-size: 12px;
      }
      .index-chip.is-ready {
        background-color: rgb(0 163 92 / 0.1);
        color: #00786f;
        .index-chip__dot { background-color: #00a35c; }
      }
      .index-chip.is-partial {
        background-color: rgb(255 200 100 / 0.2);
        color: #8a5a00;
        .index-chip__dot { background-color: #f5a623; }
      }
      .index-chip.is-processing {
        background-color: rgb(59 130 246 / 0.1);
        color: #1d4ed8;
      }
      .index-chip.is-failed {
        background-color: rgb(237 78 27 / 0.1);
        color: #c23a10;
        .index-chip__dot { background-color: #ed4e1b; }
      }
    `,
  ],
})
export class FileIndexingStatusComponent {
  // A setter, not ngOnChanges: app-table's dynamic cell assigns inputs
  // directly on the instance, which never fires ngOnChanges — the chip
  // stayed empty (12.10.2026).
  @Input() set row(file: IFile) {
    if (!file?.uuid || file.uuid === this.uuid) return;
    this.uuid = file.uuid;
    this.view$ = this.embeddingStatus.status$(file).pipe(map((s) => this.toView(s)));
  }

  private uuid?: string;

  view$?: Observable<ChipView>;

  constructor(
    private embeddingStatus: EmbeddingStatusService,
    private t: TranslateService
  ) {}


  private toView(s: EmbeddingStatus): ChipView {
    const k = (key: string, params?: Record<string, unknown>) =>
      this.t.instant(`pages.knowledgeBase.indexing.${key}`, params);
    switch (s.state) {
      case 'ready':
        return s.warning
          ? { kind: 'partial', label: k('partial'), hint: k('partialHint', { reason: s.warning }) }
          : { kind: 'ready', label: k('ready'), hint: k('readyHint') };
      case 'processing':
        if (s.pages_total) {
          const params = { done: s.pages_done || 0, total: s.pages_total };
          return { kind: 'processing', label: k('processingPages', params), hint: k('processingPagesHint', params) };
        }
        return { kind: 'processing', label: k('processing'), hint: k('processingHint') };
      case 'queued':
        return s.queue_position
          ? {
              kind: 'queued',
              label: k('queuedAt', { position: s.queue_position }),
              hint: k('queuedHint', { position: s.queue_position }),
            }
          : { kind: 'queued', label: k('queued'), hint: k('queuedHintNoPosition') };
      case 'failed':
        return { kind: 'failed', label: k('failed'), hint: k('failedHint', { reason: s.error || '' }) };
      default:
        return { kind: 'pending', label: k('pending'), hint: k('pendingHint') };
    }
  }
}
